import { useIntl, FormattedMessage } from 'react-intl';
import { Formik, Form } from 'formik';
import styled from '@emotion/styled/macro';
import {
    Button,
    Box,
    Heading,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    SimpleGrid,
    Radio,
    HStack,
    Flex,
} from '@chakra-ui/react';

import useCommonToast from '../../../../hooks/useCommonToast';
import useItemCreationWizard from '../../../../hooks/useItemCreationWizard';

import InputFormikField from 'components/forms/InputFormikField';
import RadioGroupFormikField from '../../../forms/RadioGroupFormikField';
import NumberInputFormikField from '../../../forms/NumberInputFormikField';

import { isSubmitDisabled } from '../../../../services/utils';
import { calculateRenewableCostsCurtailments } from 'services/renewables';

const CalculateCostsCurtailmentsModal = ({ subRenewableId, definition, ...rest }) => {
    const intl = useIntl();
    const { toast } = useCommonToast();

    const { projectOnOffDates } = useItemCreationWizard();

    const projectDates = projectOnOffDates.find((x) => x.subItemId === subRenewableId);
    const formOnlineDateValue = definition?.onlineDate || projectDates?.enteredOnlineDate || '';
    const formOfflineDateValue = definition?.offlineDate || projectDates?.enteredOfflineDate || '';

    const initialFormValues = {
        readonlyOnlineDate: formOnlineDateValue || '',
        readonlyOfflineDate: formOfflineDateValue || '',
        ppaPriceMode: '0', // Default to 'Fixed'
        vom: '',
        annualEscalation: '',
    };

    const formLabels = {
        onlineDate: intl.formatMessage({ id: 'common_online_date' }),
        offlineDate: intl.formatMessage({ id: 'common_offline_date' }),
        vom: intl.formatMessage({ id: 'item_creation_wizard_renewable_cost' }),
        annualEscalation: intl.formatMessage({ id: 'item_creation_wizard_annual_escalation' }),
    };

    const onFormSubmit = async (values) => {
        const { readonlyOnlineDate, readonlyOfflineDate, ppaPriceMode, vom, annualEscalation } = values;

        const payload = {
            OnlineDate: readonlyOnlineDate,
            OfflineDate: readonlyOfflineDate,
            PPAPriceMode: ppaPriceMode,
            Vom: vom,
            AnnualEscalation: annualEscalation || 0,
        };

        // Call the API to calculate the renewable costs and curtailments
        await calculateRenewableCostsCurtailments(subRenewableId, payload)
            .then(() => {
                const successMessage = intl.formatMessage({ id: 'item_creation_wizard_costs_calculation_success' });
                toast(successMessage);

                rest.onRefresh();
                rest.onClose();
            })
            .catch((error) => {
                // Handle the error and show a toast message
                const errorMessage = intl.formatMessage({ id: 'common_generic_processing_error' });
                toast({ message: errorMessage, status: 'error' });
            });
    };

    return (
        <Modal {...rest}>
            <ModalOverlay />

            <ModalContent pt={3}>
                <ModalHeader pr={12}>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="item_creation_wizard_calculate_costs_header" />
                    </Heading>
                </ModalHeader>
                <ModalCloseButton mt={4} mr={3} h={6} w={6} />

                <Formik enableReinitialize initialValues={initialFormValues} onSubmit={onFormSubmit}>
                    {({ values, isSubmitting, errors, touched }) => (
                        <Form>
                            <ModalBody borderBottom="1px" borderColor="gray.200">
                                <Flex>
                                    <FormattedMessage id="item_creation_wizard_ppa_price_mode">
                                        {(txt) => `${txt}:`}
                                    </FormattedMessage>
                                </Flex>
                                <RadioGroupFormikField mx={0} mb={3} mt={1} id="ppaPriceMode" name="ppaPriceMode">
                                    <HStack spacing={8}>
                                        <StyledRadio value={'0'}>{'Fixed'}</StyledRadio>
                                        <StyledRadio value={'1'}>{'Escalating'}</StyledRadio>
                                    </HStack>
                                </RadioGroupFormikField>
                                <SimpleGrid columns={{ xl: 2 }} spacingX={6} spacingY={4} mb={4}>
                                    <NumberInputFormikField
                                        id="vom"
                                        name="vom"
                                        label={formLabels.vom}
                                        isRequired={true}
                                    />

                                    {values.ppaPriceMode === '1' && (
                                        <NumberInputFormikField
                                            id="annualEscalation"
                                            name="annualEscalation"
                                            label={formLabels.annualEscalation}
                                            isRequired={values.ppaPriceMode === '1'}
                                            isDisabled={values.ppaPriceMode !== '1'}
                                        />
                                    )}

                                    <InputFormikField
                                        id="readonlyOnlineDate"
                                        name="readonlyOnlineDate"
                                        label={formLabels.onlineDate}
                                        isDisabled={true}
                                        isRequired
                                        isFastField
                                        info={<FormattedMessage id="item_creation_wizard_renewables_online_date" />}
                                    ></InputFormikField>

                                    <InputFormikField
                                        id="readonlyOfflineDate"
                                        name="readonlyOfflineDate"
                                        label={formLabels.offlineDate}
                                        isDisabled={true}
                                        isRequired
                                        isFastField
                                        info={<FormattedMessage id="item_creation_wizard_renewables_offline_date" />}
                                    ></InputFormikField>
                                </SimpleGrid>
                            </ModalBody>

                            <ModalFooter justifyContent="stretch" flexWrap="wrap">
                                <Box ml="auto">
                                    <Button variant="secondary" onClick={rest.onClose}>
                                        <Box as="span" textTransform="capitalize">
                                            <FormattedMessage id="common_cancel" />
                                        </Box>
                                    </Button>

                                    <Button
                                        type="submit"
                                        isDisabled={isSubmitDisabled({ errors, touched })}
                                        isLoading={isSubmitting}
                                        ml={3}
                                    >
                                        <FormattedMessage id="common_calculate" />
                                    </Button>
                                </Box>
                            </ModalFooter>
                        </Form>
                    )}
                </Formik>
            </ModalContent>
        </Modal>
    );
};

const StyledRadio = styled(Radio)`
    margin-right: 2px;
    border-color: var(--chakra-colors-green-500);

    &:hover {
        border-color: var(--chakra-colors-blue-400);
    }
`;

export default CalculateCostsCurtailmentsModal;
